import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import logger from "@/lib/logger";
import {
  Folder, User, Briefcase, Factory, Receipt, FileText, Package, Search, Loader2, CornerDownLeft,
} from "lucide-react";

const ICONS = {
  folder: Folder, user: User, briefcase: Briefcase, factory: Factory,
  receipt: Receipt, "file-text": FileText, package: Package,
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const nav = useNavigate();

  // Global Cmd/Ctrl+K listener
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else { setQ(""); setResults([]); setIdx(0); }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!open) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data.results || []);
        setIdx(0);
      } catch (e) {
        logger.debug("cmd-k search failed", e?.message || e);
      } finally { setLoading(false); }
    }, 200);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q, open]);

  const jump = useCallback((r) => {
    setOpen(false);
    if (r?.href) nav(r.href);
  }, [nav]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter")     { e.preventDefault(); if (results[idx]) jump(results[idx]); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] flex items-start justify-center pt-24 px-4"
      onClick={() => setOpen(false)}
      data-testid="cmdk-overlay"
    >
      <div
        className="w-full max-w-xl bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="cmdk-panel"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search projects, people, vendors, invoices, contracts, assets…"
            className="w-full bg-transparent outline-none text-sm text-slate-900 placeholder-slate-400"
            data-testid="cmdk-input"
          />
          {loading && <Loader2 size={14} className="text-slate-400 animate-spin" />}
          <kbd className="hidden sm:inline-flex text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">esc</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 && (
            <div className="text-[11px] text-slate-400 px-4 py-6 text-center" data-testid="cmdk-hint">
              Type at least 2 characters. Use ↑ ↓ to navigate, ↵ to jump.
            </div>
          )}
          {q.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="text-[11px] text-slate-400 px-4 py-6 text-center" data-testid="cmdk-empty">
              No matches for &quot;{q}&quot;.
            </div>
          )}
          {results.map((r, i) => {
            const Icon = ICONS[r.icon] || Folder;
            const active = i === idx;
            return (
              <button
                key={`${r.type}-${r.id}-${i}`}
                onClick={() => jump(r)}
                onMouseEnter={() => setIdx(i)}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 border-l-2 ${active ? "bg-[#F54800]/5 border-l-[#F54800]" : "border-l-transparent hover:bg-slate-50"}`}
                data-testid={`cmdk-result-${i}`}
              >
                <span className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${active ? "bg-[#F54800] text-white" : "bg-slate-100 text-slate-600"}`}>
                  <Icon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.sub}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{r.type}</span>
                {active && <CornerDownLeft size={12} className="text-[#F54800] shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
